import classNames from 'classnames'
import {memo, useCallback} from 'react'

import EntityPath from '../../../../containers/EntityPath'
import {useAppSelector} from '../../../../hooks/react-redux'
import {getBuild} from '../../../../selectors'
import type {BuildId} from '../../../../types'
import {stringifyId} from '../../../../types'
import BuildNumberContainer from '../../BuildNumber/BuildNumber.container'
import BuildStatus from '../../BuildStatus/BuildStatus.container'
import Details from '../../Details/Details'
import ListItem from '../../ListItem/ListItem'
import SvgIcon from '../../SvgIcon/SvgIcon'

import ArtifactChangeDetails from './ArtifactChangeDetails/ArtifactChangeDetails'
import ArtifactChangeFiles from './ArtifactChangeFiles'

import styles from './ArtifactChange.css'

type Props = {
  className?: string
  from: BuildId
  to: BuildId
  toggleExpandedChange: (buildId: BuildId) => unknown
  expanded?: boolean
  withSmallPadding?: boolean
  withAdvancedRow?: boolean
  withExpandableLines?: boolean
}

function ArtifactChange({
  from,
  to,
  toggleExpandedChange,
  expanded = false,
  withSmallPadding = false,
  withAdvancedRow = false,
  withExpandableLines,
}: Props) {
  const buildTypeId = useAppSelector(state => getBuild(state, from)?.buildType)
  const expandCollapseRow = useCallback(() => {
    toggleExpandedChange(from)
  }, [toggleExpandedChange, from])

  const renderRow = () =>
    from != null && (
      <>
        <ListItem
          className={classNames(styles.item, {
            [styles.advancedRow]: withAdvancedRow,
            [styles.defaultRow]: !withAdvancedRow,
          })}
          rowClassName={styles.row}
          highlightHover={!expanded}
          hideBorders
        >
          <div className={styles.pathCol}>
            <EntityPath
              className={styles.entityPath}
              buildTypeId={buildTypeId}
              projectOrBuildTypeNode={{nodeType: 'all'}}
              withCollapsing
            />
            <span className={styles.separator}>{'\u00a0/\u00a0'}</span>
            <BuildNumberContainer className={styles.buildNumber} buildId={from} hideStar />
            <BuildStatus buildId={from} />
          </div>
          <div className={styles.filesCol}>
            <ArtifactChangeFiles from={from} to={to} />
          </div>
        </ListItem>
        {withExpandableLines === true && (
          <SvgIcon
            className={classNames(styles.arrow, {
              [styles.advancedRowArrow]: withAdvancedRow,
            })}
            icon={expanded === true ? 'chevron-up' : 'chevron-down'}
          />
        )}
      </>
    )

  return (
    <div
      key={stringifyId(from)}
      className={classNames({
        [styles.panel]: expanded,
        [styles.smallPadding]: withSmallPadding,
        [styles.expandableLines]: withExpandableLines,
      })}
    >
      <Details
        level={2}
        label={stringifyId(from)}
        open={expanded}
        summary={renderRow()}
        summaryClassName={styles.button}
        onToggle={expandCollapseRow}
        imitateHeading
        renderButtonAside
      >
        <ArtifactChangeDetails from={from} to={to} withSmallPadding={withSmallPadding} />
      </Details>
    </div>
  )
}

export default memo<Props>(ArtifactChange)
